import CryptoJS from 'crypto-js';

// Security questions for client profiles
export const securityQuestions = [
  { label: "What was the name of your first pet?", value: "What was the name of your first pet?" },
  { label: "What city were you born in?", value: "What city were you born in?" },
  { label: "What was your mother's maiden name?", value: "What was your mother's maiden name?" },
  { label: "What was the name of your first school?", value: "What was the name of your first school?" },
  { label: "What was your childhood nickname?", value: "What was your childhood nickname?" },
  { label: "What is your favorite movie?", value: "What is your favorite movie?" },
  { label: "What street did you grow up on?", value: "What street did you grow up on?" },
  { label: "What was the make of your first car?", value: "What was the make of your first car?" },
  { label: "What is your favorite food?", value: "What is your favorite food?" },
  { label: "What was the name of your best friend in high school?", value: "What was the name of your best friend in high school?" }
];


/**
 * Encrypt client data using AES encryption with the provided password
 * @param {Object} data - The client data to encrypt
 * @param {string} password - The password to use for encryption
 * @returns {Object} - Contains encryptedData and encryptionMetadata
 */
export const encryptClientData = async (data, password) => {
  try {
    // Convert data to JSON string
    const jsonString = JSON.stringify(data);
    
    // Generate a random salt for key derivation
    const salt = CryptoJS.lib.WordArray.random(128/8);
    
    // Generate a random IV for encryption
    const iv = CryptoJS.lib.WordArray.random(128/8);
    
    // Derive key from password using PBKDF2
    const key = CryptoJS.PBKDF2(password, salt, {
      keySize: 256/32,
      iterations: 10000
    });
    
    // Encrypt the data
    const encrypted = CryptoJS.AES.encrypt(jsonString, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    // Combine salt, iv, and encrypted data
    const encryptedData = salt.toString() + ':' + iv.toString() + ':' + encrypted.toString();
    
    const encryptionMetadata = {
      algorithm: 'AES-256-CBC',
      keyDerivation: 'PBKDF2',
      iterations: 10000,
      timestamp: new Date().toISOString()
    };
    
    return {
      encryptedData,
      encryptionMetadata
    };
    
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt client data');
  }
};

/**
 * Decrypt client data using the provided password
 * @param {string} encryptedData - The encrypted data string
 * @param {Object} encryptionMetadata - Metadata about the encryption
 * @param {string} password - The password to use for decryption
 * @returns {Object} - The decrypted client data
 */
export const decryptClientData = async (encryptedData, encryptionMetadata, password) => {
  try {
    // Split the encrypted data to get salt, iv, and ciphertext
    const parts = encryptedData.split(':');
    if (parts.length !== 3) {
      throw new Error('Invalid encrypted data format');
    }
    
    const salt = CryptoJS.enc.Hex.parse(parts[0]);
    const iv = CryptoJS.enc.Hex.parse(parts[1]);
    const ciphertext = parts[2];
    
    // Derive the same key using the same parameters
    const key = CryptoJS.PBKDF2(password, salt, {
      keySize: 256/32,
      iterations: encryptionMetadata.iterations || 10000
    });
    
    // Decrypt the data
    const decrypted = CryptoJS.AES.decrypt(ciphertext, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    // Convert decrypted data back to string
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      throw new Error('Decryption failed - invalid password or corrupted data');
    }
    
    // Parse the JSON string back to object
    const clientData = JSON.parse(decryptedString);
    
    return clientData;
    
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Failed to decrypt client data - please check your password');
  }
};

// Helper function to build question hash mapping
export async function buildQuestionHashMap() {
  const hashMap = {};
  for (const question of securityQuestions) {
    const hash = await hashSecurityQuestion(question.value);
    hashMap[hash] = question.value;
  }
  return hashMap;
}

// Helper function to get readable question from hash
export function getQuestionFromHash(questionHash, hashMap) {
  return hashMap[questionHash] || 'Security Question';
}


/**
 * Hash a security answer using SHA-256 with salt
 * @param {string} answer - The security answer to hash
 * @returns {string} - The hashed answer with salt
 */
export const hashSecurityAnswer = async (answer) => {
  try {
    // Normalize the answer (trim, lowercase for consistency)
    const normalizedAnswer = answer.trim().toLowerCase();
    
    // Generate a random salt
    const salt = CryptoJS.lib.WordArray.random(128/8);
    
    // Hash the answer with salt using SHA-256
    const hash = CryptoJS.SHA256(normalizedAnswer + salt.toString());
    
    // Return salt:hash format for verification
    return salt.toString() + ':' + hash.toString();
    
  } catch (error) {
    console.error('Hashing failed:', error);
    throw new Error('Failed to hash security answer');
  }
};

/**
 * Verify a security answer against its hash
 * @param {string} answer - The answer to verify
 * @param {string} hashedAnswer - The stored hashed answer
 * @returns {boolean} - True if answer matches
 */
export const verifySecurityAnswer = async (answer, hashedAnswer) => {
  try {
    // Normalize the input answer
    const normalizedAnswer = answer.trim().toLowerCase();
    
    // Split the stored hash to get salt and hash
    const parts = hashedAnswer.split(':');
    if (parts.length !== 2) {
      throw new Error('Invalid hash format');
    }
    
    const salt = parts[0];
    const storedHash = parts[1];
    
    // Hash the input answer with the same salt
    const hash = CryptoJS.SHA256(normalizedAnswer + salt);
    
    // Compare hashes
    return hash.toString() === storedHash;
    
  } catch (error) {
    console.error('Verification failed:', error);
    return false;
  }
};

/**
 * Hash security questions to hide what questions were selected
 * @param {string} question - The security question to hash
 * @returns {string} - The hashed question
 */
export const hashSecurityQuestion = async (question) => {
  try {
    // Hash the question using SHA-256
    const hash = CryptoJS.SHA256(question.trim());
    return hash.toString();
  } catch (error) {
    console.error('Question hashing failed:', error);
    throw new Error('Failed to hash security question');
  }
};

/**
 * Derive password from security answers for password recovery
 * @param {Array} securityAnswers - Array of security answers
 * @param {string} userSalt - User-specific salt (could be username/email hash)
 * @returns {string} - The derived password
 */
export const derivePasswordFromSecurity = async (securityAnswers, userSalt = '') => {
  try {
    // Normalize and combine all security answers
    const combinedAnswers = securityAnswers
      .map(answer => answer.trim().toLowerCase())
      .join('|');
    
    // Create a deterministic salt from user identifier
    const salt = CryptoJS.SHA256(userSalt).toString().substring(0, 16);
    
    // Derive a password using PBKDF2
    const derivedKey = CryptoJS.PBKDF2(combinedAnswers, salt, {
      keySize: 256/32,
      iterations: 100000 // Higher iterations for password derivation
    });
    
    // Convert to a password-like string
    const password = derivedKey.toString(CryptoJS.enc.Base64).substring(0, 24);
    
    return password;
    
  } catch (error) {
    console.error('Password derivation failed:', error);
    throw new Error('Failed to derive password from security answers');
  }
};

/**
 * Simple decrypt function for recovery password
 * @param {string} encryptedData - The encrypted data string
 * @param {string} password - The password/key material to use for decryption
 * @returns {string} - The decrypted data
 */
export const decryptData = async (encryptedData, password) => {
  try {
    // Split the encrypted data to get salt, iv, and ciphertext
    const parts = encryptedData.split(':');
    if (parts.length !== 3) {
      throw new Error('Invalid encrypted data format');
    }
    
    const salt = CryptoJS.enc.Hex.parse(parts[0]);
    const iv = CryptoJS.enc.Hex.parse(parts[1]);
    const ciphertext = parts[2];
    
    // Derive the key using the same parameters as encryption
    const key = CryptoJS.PBKDF2(password, salt, {
      keySize: 256/32,
      iterations: 10000
    });
    
    // Decrypt the data
    const decrypted = CryptoJS.AES.decrypt(ciphertext, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    // Convert decrypted data back to string
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      throw new Error('Decryption failed - invalid password or corrupted data');
    }
    
    return decryptedString;
    
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Failed to decrypt data');
  }
};

/**
 * Generate a secure random password
 * @param {number} length - Length of the password (default: 16)
 * @returns {string} - A secure random password
 */
export const generateSecurePassword = (length = 16) => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  
  return password;
};

/**
 * Validate password strength
 * @param {string} password - The password to validate
 * @returns {Object} - Contains isValid boolean and strength score
 */
export const validatePasswordStrength = (password) => {
  const minLength = 8;
  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  let score = 0;
  if (password.length >= minLength) score++;
  if (hasLowerCase) score++;
  if (hasUpperCase) score++;
  if (hasNumbers) score++;
  if (hasSpecialChar) score++;
  
  return {
    isValid: password.length >= minLength,
    score: score,
    feedback: {
      length: password.length >= minLength,
      lowercase: hasLowerCase,
      uppercase: hasUpperCase,
      numbers: hasNumbers,
      special: hasSpecialChar
    }
  };
};

/**
 * Create a secure hash of sensitive data for indexing/searching
 * @param {string} data - The data to hash
 * @returns {string} - SHA-256 hash
 */
export const createSecureHash = (data) => {
  return CryptoJS.SHA256(data.trim().toLowerCase()).toString();
};